/**
 * Märkesspecifika reservdelssidor (/reservdelar/<märke>).
 *
 * Bakgrund: Search Console visar att /reservdelar får visningar på
 * märkesfrågor ("adria husvagn reservdelar", "polar reservdelar" …) men
 * ligger på position 20–35 eftersom sidan är generisk. Varje märke får
 * därför en egen sida med typskyltsplacering, vanliga delar, kända
 * svagheter och FAQ — riktigt innehåll, inte mallade textklumpar.
 */

export type PartRow = {
  /** Delkategori */
  part: string;
  /** Vad man behöver veta för att beställa rätt */
  detail: string;
  /** Ungefärligt prisspann inkl. moms, exkl. montering */
  price: string;
};

export type PartsBrand = {
  slug: string;
  name: string;
  /** husbil, husvagn eller båda */
  type: "husbil" | "husvagn" | "båda";
  tagline: string;
  intro: string;
  /** Var typskylt/chassinummer sitter på just detta märke */
  idPlate: string;
  /** Vilka underleverantörer märket bygger på — avgör vilken del som passar */
  suppliers: string[];
  parts: PartRow[];
  /** Kända svagheter per årsmodellsintervall */
  weakSpots: { title: string; text: string }[];
  faq: { q: string; a: string }[];
  keywords: string;
};

const COMMON_FAQ = (name: string): { q: string; a: string }[] => [
  {
    q: `Hur snabbt får jag svar på en ${name}-reservdel?`,
    a: "Du får pris och leveranstid inom 24 timmar på vardagar. Lagerförda delar skickas normalt inom 1–3 arbetsdagar, fabriksbeställda karossdelar tar 1–4 veckor beroende på säsong.",
  },
  {
    q: "Måste jag veta artikelnumret?",
    a: "Nej. Skicka en bild på delen plus chassinummer och årsmodell, så identifierar vi den åt dig. Bild på typskylten räcker oftast för att låsa rätt variant.",
  },
  {
    q: "Kan ni montera delen också?",
    a: "Ja, vi har egen verkstad i Kalix och monterar det vi säljer. Bor du längre bort skickar vi delen med monteringsanvisning och hjälper till per telefon.",
  },
];

export const PARTS_BRANDS: PartsBrand[] = [
  {
    slug: "adria",
    name: "Adria",
    type: "båda",
    tagline: "Adria Alpina, Altea, Adora, Matrix, Coral och Sonic",
    intro:
      "Adria bygger med egna karossprofiler men köper in tekniken från kända underleverantörer. Det betyder att en del av reservdelarna är märkesspecifika (fönster, luckor, möbelfronter, dekaler) medan resten är standardkomponenter som finns att få snabbt. Skillnaden avgör om du väntar tre dagar eller tre veckor — därför är rätt identifiering viktigare på Adria än på de flesta andra märken.",
    idPlate:
      "Typskylten sitter oftast innanför entrédörren i karmen, eller på golvet vid dörröppningen. På husbilar finns dessutom Fiats chassinummer i motorrummet och vid höger framsätesfot. Fotografera båda — Adrias eget serienummer styr inredning och kaross, Fiat-numret styr chassidelar.",
    suppliers: ["Dometic", "Thetford", "Truma", "Alde", "Polyplastic", "Seitz", "Fiat Ducato"],
    parts: [
      { part: "Fönster & takluckor", detail: "Polyplastic eller Seitz — mät bågens yttermått och läs av glasmarkeringen i hörnet.", price: "1 900–7 500 kr" },
      { part: "Servicelucka & lås", detail: "Adria använder egna luckstorlekar; mät karmens hål, inte luckan.", price: "1 200–4 200 kr" },
      { part: "Kylskåp & kyldelar", detail: "Dometic RM/RMD-serien. Modellnummer sitter bakom nedre gallret.", price: "700–14 000 kr" },
      { part: "Toalett & kassett", detail: "Thetford C200/C220/C260 — kolla knappens placering, den skiljer varianterna åt.", price: "600–5 500 kr" },
      { part: "Värme & panna", detail: "Truma Combi eller Alde 3020/3030 beroende på modellår.", price: "900–28 000 kr" },
      { part: "Vattenpump & tankar", detail: "Dränkbar pump eller tryckpump — mät slangdiameter och kolla volt.", price: "450–2 400 kr" },
      { part: "Dekaler & sidodekor", detail: "Beställs per modell och årsmodell, tillverkas mot order.", price: "800–6 000 kr" },
    ],
    weakSpots: [
      { title: "Fukt i framvägg 2006–2014", text: "Övergången mellan front och sidovägg är den vanligaste läckagepunkten på äldre Adria. Kontrollera runt strålkastarna och överkanten av vindrutan innan du beställer inredningsdelar — annars byter du något som blir blött igen." },
      { title: "Takluckor som spricker", text: "Originalluckor i akryl blir sköra efter ungefär tio norrländska vintrar. Byt hela luckan i stället för att laga; en ny lucka kostar mindre än att sanera ett fuktat tak." },
      { title: "Kylskåpets vinterkåpor", text: "Många Adria saknar rätt vinterkåpa efter ett tidigare ägarbyte. Fel kåpa ger dålig kyla på sommaren och isbildning på vintern." },
    ],
    faq: [
      {
        q: "Vilka Adria-modeller kan ni ta hem delar till?",
        a: "Alpina, Adora, Altea, Aviva, Action, Matrix, Coral, Sonic, Compact och Twin — både husvagn och husbil. Även äldre Classica och Optima går att få delar till, men karossdelar kan vara utgångna och får då lösas med alternativ.",
      },
      {
        q: "Har ni originaldelar eller kopior?",
        a: "Vi offererar originaldelen först. När originalet är utgånget eller orimligt dyrt visar vi ett likvärdigt alternativ och skriver tydligt vilket som är vad — du väljer.",
      },
      ...COMMON_FAQ("Adria"),
    ],
    keywords:
      "Adria reservdelar, Adria husvagn reservdelar, Adria husbil reservdelar, Adria husvagn tillbehör, Adria Alpina reservdelar, Adria Altea reservdelar, reservdelar Adria kaross",
  },
  {
    slug: "polar",
    name: "Polar",
    type: "båda",
    tagline: "Polar husvagn och husbil byggda för nordiskt klimat",
    intro:
      "Polar är byggd för kyla: kraftigare isolering, dubbelgolv med värmeslinga och Alde-vattenburen värme på de flesta modeller. Det gör också att reservdelarna skiljer sig från kontinentala märken — särskilt värmesystem, golvluckor och fönster. Efter att tillverkningen lades ner har delar blivit svårare att få tag på, men det mesta går fortfarande att lösa via nordiska lager och likvärdiga komponenter.",
    idPlate:
      "Typskylt i entrédörrens karm eller på höger sida av draget/chassit. Notera både modellbeteckning (t.ex. 700 Svit) och årsmodell — Polar ändrade planlösningar oftare än karosser.",
    suppliers: ["Alde", "Dometic", "Thetford", "Truma", "Polyplastic", "Al-Ko", "Fiat Ducato"],
    parts: [
      { part: "Alde värmepanna & delar", detail: "3010/3020/3030-serien. Expansionskärl, cirkulationspump och elpatron är vanligaste bytena.", price: "900–32 000 kr" },
      { part: "Konvektorer & slang", detail: "Alde-slang säljs på meter; mät längden och notera antalet krökar.", price: "300–2 800 kr" },
      { part: "Dubbelgolvsluckor", detail: "Polar-specifika mått — mät hålet och fotografera gångjärnet.", price: "700–3 400 kr" },
      { part: "Fönster & rutor", detail: "Polyplastic, ofta med kraftigare ram. Yttermått plus glasmarkering behövs.", price: "2 100–8 000 kr" },
      { part: "Chassi & bromsar", detail: "Al-Ko: bromsbackar, bromsvajer, stödben och kulkoppling är lagervaror.", price: "400–4 900 kr" },
      { part: "Markis & tillbehör", detail: "Fiamma/Thule i standardmått — enkelt att ersätta även på äldre vagnar.", price: "3 800–14 000 kr" },
    ],
    weakSpots: [
      { title: "Alde-glykol som aldrig bytts", text: "Glykolen ska bytas vartannat år. Gammal glykol fräter sönder pump och panna — kolla den innan du köper en dyr pannreservdel, ofta är det pumpen som är slut." },
      { title: "Fukt vid dubbelgolvets genomföringar", text: "Avlopp och kabelgenomföringar i dubbelgolvet är den vanligaste fuktvägen på Polar. Mät alltid vid golvvinkeln, inte bara i väggarna." },
      { title: "Stödben som kärvar", text: "Salt och vinterväg tar stödbenen. Byt hela benet i stället för att fetta ett rostigt — det är en billig del med stor säkerhetseffekt." },
    ],
    faq: [
      {
        q: "Går det fortfarande att få reservdelar till Polar?",
        a: "Ja. Tekniken kommer från Alde, Dometic, Thetford och Al-Ko som alla har delar kvar. Det som är svårast är märkesspecifik kaross- och inredningsdetalj, där vi ofta löser det med likvärdig del eller specialtillverkning.",
      },
      {
        q: "Vad kostar en Alde-service?",
        a: "Glykolbyte med kontroll av system ligger normalt på 2 500–4 500 kr beroende på systemets storlek. Byte av cirkulationspump tillkommer med runt 2 000–3 500 kr inklusive del.",
      },
      ...COMMON_FAQ("Polar"),
    ],
    keywords:
      "Polar reservdelar, Polar husvagn reservdelar, Polar husbil delar, Alde reservdelar, Polar 700 svit delar, reservdelar Polar husvagn",
  },
  {
    slug: "kabe",
    name: "Kabe",
    type: "båda",
    tagline: "Kabe Royal, Ädelsten, Estate och Imperial",
    intro:
      "Kabe har högst andel egentillverkade komponenter av de nordiska märkena: väggar, möbler, luckor och en stor del av vattensystemet är Kabes egna. Därför är chassinummer och exakt modellbeteckning avgörande — samma modellnamn kan ha två olika luckmått mellan två årsmodeller. Å andra sidan har Kabe god delförsörjning även bakåt i tiden.",
    idPlate:
      "Typskylten sitter på draget eller innanför entrédörren. Kabes serienummer står också ofta instämplat i garderobens karm. Fotografera hela skylten, inte bara numret — årsmodell och totalvikt behövs för bromsdelar.",
    suppliers: ["Alde", "Dometic", "Thetford", "Al-Ko", "BPW", "Kabe egen tillverkning"],
    parts: [
      { part: "Alde värmesystem", detail: "Kabe kör vattenburen värme genomgående. Panna, pump, kärl och konvektorer.", price: "900–34 000 kr" },
      { part: "Fönster & ventiler", detail: "Kabe-specifika ramar på flera årsmodeller — mät karmen invändigt.", price: "2 300–8 500 kr" },
      { part: "Möbelluckor & beslag", detail: "Beställs på dekor och årsmodell; dekorerna byts ungefär vart femte år.", price: "600–4 000 kr" },
      { part: "Golvvärme & termostat", detail: "Elslinga i golv med separat termostat — vanligt fel är givaren, inte slingan.", price: "800–5 600 kr" },
      { part: "Chassi & broms", detail: "Al-Ko eller BPW beroende på år. Totalvikt avgör vilken bromsback som passar.", price: "450–5 200 kr" },
      { part: "Ytterdörr & lås", detail: "Kabes egna dörrar — cylinder och handtag finns som separata delar.", price: "900–12 000 kr" },
    ],
    weakSpots: [
      { title: "Termostatgivare i golvvärmen", text: "Golvvärmen slutar fungera långt oftare på grund av en trasig givare än på grund av en trasig slinga. Mät givaren först, den kostar en bråkdel." },
      { title: "Fönsterhasp och gasfjädrar", text: "Gasfjädrarna i fönstren tappar kraft efter 6–8 år och fönstret slår igen av sig självt. Billig del, stor irritation." },
      { title: "Fukt runt bakvägg och bakljus", text: "På Royal-modellerna är genomföringarna vid bakljusen en klassisk läckagepunkt. Kontrollera innan du renoverar inredningen." },
    ],
    faq: [
      {
        q: "Kan ni ta hem originaldelar från Kabe?",
        a: "Ja, via våra branschkontakter. Kabe kräver ofta serienummer för att bekräfta rätt variant, så skicka en bild på typskylten direkt i förfrågan så går det snabbare.",
      },
      {
        q: "Hur vet jag vilken dekor min inredning har?",
        a: "Dekornamnet står oftast på en etikett på insidan av en möbellucka eller i garderoben. Saknas den fotograferar du en lucka i dagsljus, så matchar vi mot dekorkartan.",
      },
      ...COMMON_FAQ("Kabe"),
    ],
    keywords:
      "Kabe reservdelar, Kabe husvagn reservdelar, Kabe Royal delar, Kabe Ädelsten reservdelar, reservdelar Kabe husbil, Kabe lucka fönster",
  },
  {
    slug: "rapido",
    name: "Rapido",
    type: "husbil",
    tagline: "Vi är Rapido-återförsäljare — bäst delförsörjning av alla märken",
    intro:
      "Rapido är vårt eget märke som återförsäljare, och det märks på delförsörjningen: vi beställer direkt från fabrik i Mayenne och har både katalogåtkomst och sprängskisser. Det gör att vi kan identifiera en del utifrån en bild och årsmodell även när kunden inte hittar något nummer alls.",
    idPlate:
      "Rapidos typskylt sitter innanför entrédörren eller i garaget. Fiat- eller Mercedes-chassinumret finns i motorrummet respektive vid förarstolen. Modellbeteckningen (t.ex. 866F, V55, 9005dfH) styr inredningsdelarna.",
    suppliers: ["Dometic", "Thetford", "Truma", "Alde", "Fiat Ducato", "Mercedes Sprinter", "Seitz"],
    parts: [
      { part: "Belysning & spotlights", detail: "LED-spots och list — vi har sprängskisser per modellår.", price: "300–2 400 kr" },
      { part: "Kontrollpanel & elcentral", detail: "Schaudt eller CBE beroende på årsmodell.", price: "1 400–11 000 kr" },
      { part: "Fönster, luckor & garageportar", detail: "Seitz/Dometic-ramar plus Rapido-specifika garageluckor.", price: "1 600–9 800 kr" },
      { part: "Vattensystem & pumpar", detail: "Tank, pump, boiler och blandare — ofta lagervara.", price: "400–7 200 kr" },
      { part: "Möbler & lucksnitt", detail: "Fabriksbeställs på dekor och modellår.", price: "700–6 500 kr" },
      { part: "Chassi Fiat/Mercedes", detail: "Bromsar, fjädring, luftfjädring och kylare via ordinarie chassikanal.", price: "600–18 000 kr" },
    ],
    weakSpots: [
      { title: "Garageportens gasfjädrar", text: "Tunga garageluckor tar ut gasfjädrarna på 5–7 år. Byt parvis, aldrig en i taget." },
      { title: "Takluckans tätningslist", text: "Listen krymper i kyla och släpper in vatten vid kraftigt regn. Byt list innan du byter hela luckan." },
      { title: "Schaudt-panelens givare", text: "Felaktig tankvisning beror nästan alltid på givaren i tanken, inte på panelen." },
    ],
    faq: [
      {
        q: "Är ni auktoriserad Rapido-verkstad?",
        a: "Vi är Rapido-återförsäljare med egen verkstad i Kalix och beställer delar direkt från fabrik. Du hittar hela vårt Rapido-utbud och modellguider under /rapido.",
      },
      {
        q: "Hur hittar jag rätt del i Rapidos katalog?",
        a: "Skicka modellbeteckning, årsmodell och en bild. Vi slår upp delen i fabrikens sprängskiss och skickar tillbaka artikelnummer, pris och leveranstid.",
      },
      ...COMMON_FAQ("Rapido"),
    ],
    keywords:
      "Rapido reservdelar, Rapido husbil delar, Rapido spare parts, Rapido återförsäljare Sverige, Rapido 866F delar, Rapido V55 reservdelar",
  },
  {
    slug: "dethleffs",
    name: "Dethleffs",
    type: "båda",
    tagline: "Dethleffs Magic, Esprit, Globebus, Trend och Nomad",
    intro:
      "Dethleffs tillhör Erwin Hymer Group, vilket är goda nyheter för dig som söker delar: koncernen har ett gemensamt reservdelslager och de flesta komponenter finns kvar långt bakåt i tiden. Det som brukar strula är att samma modellnamn använts i tjugo år — utan årsmodell blir det fel del.",
    idPlate:
      "Typskylt innanför entrédörren eller i främre servicelucka. På husbilar finns Fiat/Ford-numret i motorrummet. Notera även 'Fahrzeug-Ident' på skylten, det är numret fabriken söker på.",
    suppliers: ["Dometic", "Thetford", "Truma", "Al-Ko", "Fiat Ducato", "Ford Transit", "Seitz"],
    parts: [
      { part: "Truma Combi & delar", detail: "Combi 4/6, elpatron, brännare och styrkort.", price: "900–26 000 kr" },
      { part: "Fönster & takluckor", detail: "Seitz S4/S7 — glasmarkeringen anger exakt variant.", price: "1 800–7 800 kr" },
      { part: "Serviceluckor", detail: "Standardiserade mått inom Hymer-koncernen, oftast lagervara.", price: "1 100–4 300 kr" },
      { part: "Möbelfronter", detail: "Beställs på dekornamn; äldre dekorer kan ersättas med foliering.", price: "600–4 800 kr" },
      { part: "Al-Ko chassi", detail: "Bromsback, vajer, stödben och stötdämpare.", price: "400–4 600 kr" },
      { part: "Kylskåp", detail: "Dometic absorption eller kompressor beroende på modellår.", price: "700–15 000 kr" },
    ],
    weakSpots: [
      { title: "Alkovens fönstertätning", text: "På äldre Magic-alkover läcker fönstret i alkoven före allt annat. Mät fukt i alkovgolvet vid varje kontroll." },
      { title: "Truma Combi styrkort", text: "Ett återkommande fel efter 10 år är styrkortet, inte brännaren. Felkoden på panelen avgör." },
      { title: "Vattenlås som fryser sönder", text: "Dethleffs har ofta plastvattenlås under diskbänken som spricker vid frysning. Töm systemet ordentligt inför vinterförvaring." },
    ],
    faq: [
      {
        q: "Kan ni få tag på delar till gamla Dethleffs?",
        a: "Ofta ja — Hymer-koncernen har lång delförsörjning. För modeller före 2005 kan karossdelar vara utgångna, men teknik och inredningsbeslag går nästan alltid att lösa.",
      },
      {
        q: "Passar Hymer-delar på min Dethleffs?",
        a: "Många tekniska komponenter delas inom koncernen, men karossdelar gör det inte. Vi kontrollerar mot fabrikens katalog innan vi offererar, så du inte köper en del som inte går att montera.",
      },
      ...COMMON_FAQ("Dethleffs"),
    ],
    keywords:
      "Dethleffs reservdelar, Dethleffs husbil delar, Dethleffs Magic reservdelar, Dethleffs husvagn reservdelar, Hymer koncern reservdelar",
  },
  {
    slug: "hobby",
    name: "Hobby",
    type: "båda",
    tagline: "Hobby De Luxe, Premium, Prestige och Excellent",
    intro:
      "Hobby är ett av Europas mest sålda husvagnsmärken, vilket gör delförsörjningen bland de bästa på marknaden — det mesta finns på lager i Tyskland med korta ledtider. Utmaningen är i stället mängden varianter: Hobby uppdaterar detaljer mitt i modellåret, så bilden på delen är viktigare än modellnamnet.",
    idPlate:
      "Typskylten sitter på framväggen vid draget eller innanför dörren. Hobby anger ett 'Fahrgestellnummer' som fabriken söker på — fotografera hela skylten.",
    suppliers: ["Truma", "Dometic", "Thetford", "Al-Ko", "Seitz", "Polyplastic"],
    parts: [
      { part: "Fönster & takluckor", detail: "Seitz eller Polyplastic beroende på modellserie.", price: "1 700–7 400 kr" },
      { part: "Truma värme & delar", detail: "S-serien och Combi. Brännarrör och tändelektrod är vanliga slitdelar.", price: "500–24 000 kr" },
      { part: "Möbelluckor & bordsben", detail: "Vanliga slitdelar, oftast lagervara i Tyskland.", price: "500–3 600 kr" },
      { part: "Ytterdörr & lås", detail: "Hobby-specifika dörrar; cylinder och handtag finns separat.", price: "800–11 500 kr" },
      { part: "Al-Ko chassi & stödben", detail: "Standarddelar med snabb leverans.", price: "400–4 400 kr" },
      { part: "Markis & förtältslist", detail: "Fiamma/Thule plus listprofiler på meter.", price: "300–13 000 kr" },
    ],
    weakSpots: [
      { title: "Takluckans krypande sprickor", text: "Akrylluckor som är över tio år spricker vid gångjärnet. Kontrollera underifrån i motljus." },
      { title: "Golvskiva vid entrédörren", text: "Insteget är den mest belastade punkten. Mjukt golv där betyder ofta läckage via dörrkarmen." },
      { title: "Bordsbenets låsning", text: "Ett klassiskt Hobby-fel som gör att bordet sjunker. Delen kostar under tusenlappen." },
    ],
    faq: [
      {
        q: "Har Hobby snabba leveranstider?",
        a: "Ja, Hobby har ett av branschens bästa centrallager. Lagerförda delar är ofta framme inom en vecka, även karossdetaljer.",
      },
      {
        q: "Behöver jag modellnamn eller chassinummer?",
        a: "Chassinummer plus årsmodell räcker. Hobby byter detaljer mitt i modellåret, så numret slår modellnamnet varje gång.",
      },
      ...COMMON_FAQ("Hobby"),
    ],
    keywords:
      "Hobby reservdelar, Hobby husvagn reservdelar, Hobby De Luxe delar, Hobby husbil reservdelar, Hobby fönster taklucka",
  },
  {
    slug: "knaus",
    name: "Knaus",
    type: "båda",
    tagline: "Knaus Sport, Südwind, Sky Ti och Van Ti",
    intro:
      "Knaus Tabbert har egen delförsörjning med bra täckning bakåt, men kombinerar många leverantörer i samma fordon. En Knaus från samma år kan ha antingen Truma eller Alde, antingen Seitz eller Polyplastic. Därför är bilden på den faktiska delen viktigare här än på nästan något annat märke.",
    idPlate:
      "Typskylt i entrédörrens karm eller på framväggen. Husbilar har dessutom Fiat- eller MAN-chassinummer. Notera 'KNA' i början av vagnnumret — det bekräftar rätt fabrik.",
    suppliers: ["Truma", "Alde", "Dometic", "Thetford", "Seitz", "Polyplastic", "Al-Ko", "Fiat Ducato"],
    parts: [
      { part: "Värmesystem", detail: "Truma Combi eller Alde — kontrollera vilket du har innan beställning.", price: "800–30 000 kr" },
      { part: "Fönster", detail: "Seitz eller Polyplastic. Glasmarkeringen i hörnet avgör.", price: "1 900–8 200 kr" },
      { part: "Serviceluckor & garage", detail: "Modellspecifika på Sky/Van Ti — mät karmen.", price: "1 200–5 400 kr" },
      { part: "Vattensystem", detail: "Tank, pump, boiler och blandare, mestadels standardkomponenter.", price: "400–6 800 kr" },
      { part: "Möbler & beslag", detail: "Fabriksbeställning på dekor och modellår.", price: "600–5 000 kr" },
      { part: "Chassi & bromsar", detail: "Al-Ko för vagn, Fiat/MAN för husbil.", price: "450–16 000 kr" },
    ],
    weakSpots: [
      { title: "Duschkabinens fästen", text: "Vibrationer på grusväg lossar fästena i duschen. Billig del, men vatten som rinner utanför kabinen ger dyra fuktskador." },
      { title: "Fönsterrullgardin som fastnar", text: "Rullgardinerna kärvar när fjädern tappat spänst. Hela kassetten byts, inte bara duken." },
      { title: "Genomföring för TV-kabel", text: "En ofta glömd läckagepunkt på Südwind. Kontrollera tätningen vid årlig fuktkontroll." },
    ],
    faq: [
      {
        q: "Hur vet jag om jag har Truma eller Alde?",
        a: "Alde har en vattenpanna med synliga rör och konvektorer längs väggarna. Truma Combi är en luftvärmare med runda utblås i golvet eller möblerna. Skicka en bild så avgör vi åt dig.",
      },
      {
        q: "Går det att få delar till äldre Knaus?",
        a: "Teknikdelar ja, karossdelar beror på årsmodell. Vi kontrollerar hos Knaus Tabbert och föreslår likvärdig lösning när originalet är slut.",
      },
      ...COMMON_FAQ("Knaus"),
    ],
    keywords:
      "Knaus reservdelar, Knaus husvagn reservdelar, Knaus Südwind delar, Knaus Sky Ti reservdelar, Knaus Tabbert reservdelar",
  },
  {
    slug: "fiat-ducato",
    name: "Fiat Ducato",
    type: "husbil",
    tagline: "Chassidelar till husbilar byggda på Ducato",
    intro:
      "Nio av tio husbilar i Sverige står på ett Fiat Ducato-chassi. Det är bra: chassidelar är standardvaror med hög tillgänglighet och rimliga priser, oavsett vilket märke som byggt påbyggnaden. Det du behöver hålla isär är vilken generation du har — X250 (2006–2014), X290 (2014–2021) och den nya elektriska plattformen har olika delar trots att bilarna ser lika ut.",
    idPlate:
      "Chassinumret sitter i motorrummet på höger fjädertorn och vid höger framsätesfot under en plastlucka. Notera även motorkoden på ventilkåpan — den avgör bromsar och kylsystem.",
    suppliers: ["Fiat Professional", "Sachs", "Brembo", "Bosch", "Al-Ko AMC", "Goldschmitt"],
    parts: [
      { part: "Bromsar", detail: "Skivor och belägg skiljer mellan light och heavy chassi — totalvikten avgör.", price: "1 200–7 400 kr" },
      { part: "Fjädring & stötdämpare", detail: "Förstärkta alternativ eller luftfjädring bak vid tung påbyggnad.", price: "2 400–34 000 kr" },
      { part: "Kylare & termostat", detail: "Vanligt fel efter 10 år, särskilt på bilar som körts med tung last i backe.", price: "1 800–8 500 kr" },
      { part: "Backkamera & elektronik", detail: "Ofta eftermonterat av påbyggaren — bild på enheten behövs.", price: "900–7 000 kr" },
      { part: "Karossplåt & strålkastare", detail: "Front, stötfångare och lyktor är standarddelar med bra tillgång.", price: "1 100–12 000 kr" },
      { part: "Service & filter", detail: "Olje-, luft-, bränsle- och kupéfilter i satser.", price: "700–2 600 kr" },
    ],
    weakSpots: [
      { title: "Rost i bakre hjulhus X250", text: "Modellåren 2007–2011 rostar i bakskärmens innerkant. Åtgärda tidigt, det växer under påbyggnadens list." },
      { title: "Överbelastad bakaxel", text: "Många husbilar körs nära max bakaxeltryck. Slitna bakfjädrar ger vandring på vägen — luftfjädring löser det permanent." },
      { title: "AdBlue-givare på nyare modeller", text: "Givarfel ger startspärr. Felkoden avgör om det är givaren eller pumpen." },
    ],
    faq: [
      {
        q: "Kan ni serva Fiat-chassit också, inte bara påbyggnaden?",
        a: "Ja. Vi hanterar bromsar, fjädring, kylsystem och service på Ducato-chassit i vår verkstad i Kalix, och kombinerar gärna med fuktkontroll av påbyggnaden i samma besök.",
      },
      {
        q: "Lönar sig luftfjädring bak?",
        a: "På en husbil som ofta körs fullastad eller drar släp: ja. Den minskar vandring, förbättrar komforten och avlastar bakfjädrarna. Räkna med 22 000–34 000 kr monterat beroende på system.",
      },
      ...COMMON_FAQ("Fiat Ducato"),
    ],
    keywords:
      "Fiat Ducato reservdelar husbil, Ducato chassidelar, husbil bromsar Ducato, luftfjädring husbil, Ducato X250 X290 delar",
  },
];

export function getPartsBrand(slug: string): PartsBrand | undefined {
  return PARTS_BRANDS.find((b) => b.slug === slug.toLowerCase());
}
